/**
 * API client for Invoice endpoints
 */

import apiClient from "./client";
import type {
  Invoice,
  InvoiceWithDetails,
  InvoiceListResponse,
  InvoiceHistoryResponse,
  InvoicePreviewResponse,
  ListInvoicesParams,
  GenerateInvoiceRequest,
  BatchGenerateRequest,
  VoidInvoiceRequest,
  CreateCreditMemoRequest,
  VoidCreditMemoRequest,
  InvoiceMessageResponse,
  CreditMemo,
} from "@/types/invoice";

/**
 * Invoices API endpoints
 */
export const invoicesApi = {
  /**
   * List invoices with pagination and filters.
   * `class_ids` is serialized as a CSV string — the backend splits on `,`
   * to allow this endpoint to be hit from a plain GET URL.
   */
  list: (params?: ListInvoicesParams) => {
    const { class_ids, ...rest } = params ?? {};
    const queryParams: Record<string, unknown> = { ...rest };
    if (class_ids && class_ids.length > 0) {
      queryParams.class_ids = class_ids.join(",");
    }
    return apiClient.get<InvoiceListResponse>("/api/invoices/v1/", {
      params: queryParams,
    });
  },

  /**
   * Get outstanding invoices
   */
  getOutstanding: (params?: { family_id?: string; campus_id?: string; page?: number; limit?: number }) =>
    apiClient.get<InvoiceListResponse>("/api/invoices/v1/outstanding", { params }),

  /**
   * Get invoice by ID with full details
   */
  get: (id: string) =>
    apiClient.get<InvoiceWithDetails>(`/api/invoices/v1/${id}`),

  /**
   * Manually generate invoice (requires fee_items)
   */
  generate: (data: GenerateInvoiceRequest) =>
    apiClient.post<InvoiceWithDetails>("/api/invoices/v1/generate", data),

  /**
   * Generate invoice for a family from their active enrollments
   * Automatically calculates fees from enrolled classes
   */
  generateForFamily: (data: { family_id: string; billing_month: string }) =>
    apiClient.post<InvoiceWithDetails | { message: string }>("/api/invoices/v1/generate-for-family", data),

  /**
   * Batch generate monthly invoices
   * Uses extended timeout (2 minutes) as this can process many families
   */
  batchGenerate: (data: BatchGenerateRequest) =>
    apiClient.post<InvoiceMessageResponse>("/api/invoices/v1/batch-generate", data, {
      timeout: 120000,
    }),

  /**
   * Void invoice
   */
  void: (id: string, data: VoidInvoiceRequest) =>
    apiClient.post<InvoiceMessageResponse>(`/api/invoices/v1/${id}/void`, data),

  /**
   * Create credit memo. `data.category` and `data.line_item_id` are
   * optional (migration 140).
   */
  createCreditMemo: (id: string, data: CreateCreditMemoRequest) =>
    apiClient.post<CreditMemo>(`/api/invoices/v1/${id}/credit-memo`, data),

  /**
   * Void a previously issued credit memo. Restores its amount back
   * to the parent invoice's outstanding_balance and stamps voided_*
   * fields on the credit memo for audit.
   */
  voidCreditMemo: (
    invoiceId: string,
    creditMemoId: string,
    data: VoidCreditMemoRequest,
  ) =>
    apiClient.post<CreditMemo>(
      `/api/invoices/v1/${invoiceId}/credit-memo/${creditMemoId}/void`,
      data,
    ),

  /**
   * Resend invoice email to primary guardian
   */
  resendEmail: (id: string) =>
    apiClient.post<InvoiceMessageResponse>(`/api/invoices/v1/${id}/resend-email`),

  /**
   * Preview invoice for a family (read-only, no DB writes)
   */
  preview: (params: { family_id: string; billing_month: string }) =>
    apiClient.get<InvoicePreviewResponse>("/api/invoices/v1/preview", {
      params,
      _skipToast: true,
    } as Record<string, unknown>),

  /**
   * Get invoice history
   */
  getHistory: (id: string) =>
    apiClient.get<InvoiceHistoryResponse>(`/api/invoices/v1/${id}/history`),

  /**
   * Download invoice PDF
   */
  downloadPdf: (id: string) =>
    apiClient.get<Blob>(`/api/invoices/v1/${id}/pdf`, {
      responseType: "blob",
    }),

  /**
   * Export all invoices (fetches all records for CSV export).
   * Same `class_ids` CSV serialization as `list` so the export honors
   * an active class filter.
   */
  exportAll: async (params?: Omit<ListInvoicesParams, 'page' | 'limit'>) => {
    const { class_ids, ...rest } = params ?? {};
    const baseParams: Record<string, unknown> = { ...rest };
    if (class_ids && class_ids.length > 0) {
      baseParams.class_ids = class_ids.join(",");
    }

    const allInvoices: Invoice[] = [];
    let page = 1;
    const limit = 100;
    let hasMore = true;

    while (hasMore) {
      const response = await apiClient.get<InvoiceListResponse>("/api/invoices/v1/", {
        params: { ...baseParams, page, limit },
      });
      const data = response.data;
      allInvoices.push(...data.invoices);

      hasMore = page < data.meta.total_pages;
      page++;
    }

    return { invoices: allInvoices, meta: { total: allInvoices.length, page: 1, limit: allInvoices.length, total_pages: 1 } };
  },
};

export default invoicesApi;
